import React, { useCallback, useEffect, useState } from 'react';
import { message, Spin } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import { priorYearCapitalService, BasisMethod, CapitalSummary } from './PriorYearCapitalService';
import styles from './PriorYearCapital.module.css';

function fmtCurrency(n: number): string {
  if (n == null) return '—';
  const abs = Math.abs(n).toLocaleString('en-US');
  return (n < 0 ? '(' : '') + '$' + abs + (n < 0 ? ')' : '');
}

const BASIS_OPTIONS: { value: BasisMethod; label: string }[] = [
  { value: 'tax', label: 'Tax Basis' },
  { value: 'gaap', label: 'GAAP Basis' },
  { value: '704b', label: '704(b) Basis' },
];

export const PriorYearCapital: React.FC = () => {
  const { selectedPartnership, taxYear } = usePartnershipContext();
  const [basisMethod, setBasisMethod] = useState<BasisMethod>('tax');
  const [summary, setSummary] = useState<CapitalSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (partnershipId: string, year: string, basis: BasisMethod) => {
    setLoading(true);
    try {
      const data = await priorYearCapitalService.getSummary(partnershipId, year, basis);
      setSummary(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === undefined) {
        setSummary({ basisMethod: basis, taxYear: year, rows: [], totalBeginning: 0, totalContributions: 0, totalDistributions: 0, totalIncomeLoss: 0, totalEnding: 0 });
      } else {
        message.error('Failed to load capital account data');
        setSummary(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPartnership?.id) {
      fetchData(selectedPartnership.id, taxYear, basisMethod);
    } else {
      setSummary(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnership?.id, taxYear, basisMethod]);

  const rows = summary?.rows ?? [];

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Prior Year Capital</h1>
          {selectedPartnership && (
            <p className={styles.pageSub}>{selectedPartnership.name} · Partner capital account balances</p>
          )}
        </div>
        <div className={styles.headActions}>
          <select
            className={styles.basisSelect}
            value={basisMethod}
            onChange={(e) => setBasisMethod(e.target.value as BasisMethod)}
          >
            {BASIS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button className={styles.btnOutline} onClick={() => message.info('Export coming soon')}>
            Export
          </button>
        </div>
      </div>

      {!selectedPartnership ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><BarChartOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
          <div className={styles.emptyText}>Select a client and partnership to view capital accounts</div>
        </div>
      ) : (
        <>
          {summary && (
            <div className={styles.kpiRow}>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Beginning Capital</div>
                <div className={styles.kpiValue}>{fmtCurrency(summary.totalBeginning)}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Contributions</div>
                <div className={styles.kpiValue}>{fmtCurrency(summary.totalContributions)}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Distributions</div>
                <div className={styles.kpiValue}>{fmtCurrency(summary.totalDistributions)}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Income / Loss</div>
                <div className={summary.totalIncomeLoss >= 0 ? styles.kpiValuePos : styles.kpiValueNeg}>
                  {fmtCurrency(summary.totalIncomeLoss)}
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Ending Capital</div>
                <div className={styles.kpiValue}>{fmtCurrency(summary.totalEnding)}</div>
              </div>
            </div>
          )}

          {loading ? (
            <div className={styles.loadingState}><Spin /></div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Type</th>
                    <th style={{ width: 80, textAlign: 'right' }}>Ownership %</th>
                    <th style={{ width: 150, textAlign: 'right' }}>Beginning Capital</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Contributions</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Distributions</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Income / Loss</th>
                    <th style={{ width: 150, textAlign: 'right' }}>Ending Capital</th>
                  </tr>
                </thead>
                <tbody>
                  {!summary || rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={styles.emptyCell}>
                        No capital account data found for this partnership and tax year.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {rows.map((row) => (
                        <tr key={row.id} className={styles.tableRow}>
                          <td>{row.partnerName}</td>
                          <td>
                            <span className={row.partnerType === 'GP' ? styles.tagGP : styles.tagLP}>
                              {row.partnerType}
                            </span>
                          </td>
                          <td className={styles.cellMono}>{row.ownershipPercent.toFixed(2)}%</td>
                          <td className={styles.cellMono}>{fmtCurrency(row.beginningCapital)}</td>
                          <td className={styles.cellMono}>{fmtCurrency(row.contributions)}</td>
                          <td className={styles.cellMono}>{fmtCurrency(row.distributions)}</td>
                          <td className={styles.cellMono}>{fmtCurrency(row.incomeLoss)}</td>
                          <td className={styles.cellMono}>{fmtCurrency(row.endingCapital)}</td>
                        </tr>
                      ))}
                      <tr className={styles.totalRow}>
                        <td colSpan={2}><strong>Total</strong></td>
                        <td className={styles.cellMono}>100.00%</td>
                        <td className={styles.cellMono}>{fmtCurrency(summary.totalBeginning)}</td>
                        <td className={styles.cellMono}>{fmtCurrency(summary.totalContributions)}</td>
                        <td className={styles.cellMono}>{fmtCurrency(summary.totalDistributions)}</td>
                        <td className={styles.cellMono}>{fmtCurrency(summary.totalIncomeLoss)}</td>
                        <td className={styles.cellMono}>{fmtCurrency(summary.totalEnding)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
              {summary && rows.length > 0 && (
                <div className={styles.tableFooter}>
                  {rows.length} partner{rows.length !== 1 ? 's' : ''} · {BASIS_OPTIONS.find(o => o.value === basisMethod)?.label}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
